/**
 * The X rules that the API, the web app and the native apps share (spec
 * docs/superpowers/specs/2026-10-22-music24-x-connector-design.md). The file has no imports, so
 * every project can use it.
 */

/** The most characters of an X post. A Premium account can post more; Music24 uses 280 for all. */
export const X_POST_TEXT_LIMIT = 280;

/** X counts every link as 23 characters (t.co), whatever its real length. */
export const X_URL_LENGTH = 23;

const URL_PATTERN = /https?:\/\/\S+/g;

/**
 * The code points that X counts as 1 (twitter-text v3: Latin, Greek, Cyrillic, Hebrew, Arabic and
 * the other scripts up to U+10FF, and some punctuation). X counts every other code point as 2: CJK,
 * Hangul, emoji, and symbols such as `…` (U+2026).
 */
export const X_LIGHT_RANGES: ReadonlyArray<readonly [number, number]> = [
  [0x0000, 0x10ff],
  [0x2000, 0x200d],
  [0x2010, 0x201f],
  [0x2032, 0x2037],
];

function isLight(codePoint: number): boolean {
  return X_LIGHT_RANGES.some(([first, last]) => codePoint >= first && codePoint <= last);
}

function isRegionalIndicator(codePoint: number): boolean {
  return codePoint >= 0x1f1e6 && codePoint <= 0x1f1ff;
}

/**
 * A code point that joins the one before it into one character: a zero-width joiner (and the code
 * point after it), a variation selector, a skin tone, the keycap mark, a combining mark, an emoji
 * tag.
 */
function extendsCharacter(codePoint: number): boolean {
  return (
    codePoint === 0x200d ||
    codePoint === 0xfe0e ||
    codePoint === 0xfe0f ||
    codePoint === 0x20e3 ||
    (codePoint >= 0x1f3fb && codePoint <= 0x1f3ff) ||
    (codePoint >= 0x0300 && codePoint <= 0x036f) ||
    (codePoint >= 0xe0020 && codePoint <= 0xe007f)
  );
}

/**
 * The characters of `text` as code point lists. An emoji sequence (joined parts, a skin tone, a
 * flag of two regional indicators, a keycap) is one character. The iOS and Android copies use the
 * same steps, so the three counts agree.
 */
function xCharacters(text: string): number[][] {
  const characters: number[][] = [];
  let joinNext = false;
  for (const char of Array.from(text.normalize('NFC'))) {
    const codePoint = char.codePointAt(0) ?? 0;
    const last = characters[characters.length - 1];
    const pairsFlag =
      last !== undefined && last.length === 1 && isRegionalIndicator(last[0] ?? 0) && isRegionalIndicator(codePoint);
    if (last !== undefined && (joinNext || extendsCharacter(codePoint) || pairsFlag)) {
      last.push(codePoint);
    } else {
      characters.push([codePoint]);
    }
    joinNext = codePoint === 0x200d;
  }
  return characters;
}

/**
 * The length of `text` as X counts it. Each link is 23. A character whose code points are all in
 * `X_LIGHT_RANGES` counts 1 for each code point; every other character (an emoji sequence, CJK)
 * counts 2.
 */
export function xTextLength(text: string): number {
  let links = 0;
  const rest = text.replace(URL_PATTERN, () => {
    links += 1;
    return '';
  });
  let length = 0;
  for (const character of xCharacters(rest)) {
    length += character.every(isLight) ? character.length : 2;
  }
  return length + links * X_URL_LENGTH;
}

/** The API posts that one X account sent, and has scheduled, in this UTC month. */
export interface XPostsUsage {
  used: number;
  limit: number;
  /** ISO time: the first instant of the next UTC month. */
  resetsAt: string;
}

/** The first day of the UTC month of `at`, as `YYYY-MM-01`. */
export function xMonthStart(at: Date): string {
  const month = String(at.getUTCMonth() + 1).padStart(2, '0');
  return `${at.getUTCFullYear()}-${month}-01`;
}

/** The first instant of the UTC month after the month of `at`. */
export function xMonthResetsAt(at: Date): Date {
  return new Date(Date.UTC(at.getUTCFullYear(), at.getUTCMonth() + 1, 1));
}
